import React, { useState, useEffect } from 'react';
import { personalInfo, heroData } from '../../data/portfolioData';

const HomePage = ({ setActiveSection }) => {
  const [hasAnimated, setHasAnimated] = useState(false);
  
  useEffect(() => {
    // Trigger animation once component mounts
    setHasAnimated(true);
  }, []);
  
  return (
    <div className="relative overflow-hidden py-20 md:py-32">
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-purple-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute top-1/3 left-1/2 w-40 h-40 bg-cyan-300/10 rounded-full filter blur-2xl"></div>
        
        {/* Grid overlay */}
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[length:50px_50px]"></div>
      </div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center">
          {/* Tech-themed animated text intro */}
          <div className="overflow-hidden mb-2">
            <div className={`transform ${hasAnimated ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} transition-all duration-700 ease-out font-mono text-blue-400`}>
              &lt;developer&gt;
            </div>
          </div>
          
          <h1 
            className={`text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600 transform ${hasAnimated ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} transition-all duration-1000 delay-300 ease-out`}
          >
            {personalInfo.name}
          </h1>
          
          <div className="overflow-hidden mb-8">
            <p 
              className={`text-xl md:text-2xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto transform ${hasAnimated ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} transition-all duration-1000 delay-500 ease-out`}
            >
              {heroData.subheading}
            </p>
          </div>
          
          <div className={`mt-8 transform ${hasAnimated ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} transition-all duration-1000 delay-700 ease-out`}>
            <div className="overflow-hidden mb-8">
              <div className="font-mono text-blue-400">
                &lt;/developer&gt;
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href={personalInfo.resume} 
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium inline-flex items-center space-x-2 transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/20"
              >
                <span>Download Resume</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
              </a>
              <button 
                onClick={() => setActiveSection('work')}
                className="border border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-400 text-gray-700 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400 px-6 py-3 rounded-md font-medium transition-colors duration-200"
              >
                Explore My Work
              </button>
            </div>
          </div>
        </div>
        
        {/* Tech stack labels */}
        <div className={`mt-16 ${hasAnimated ? 'opacity-100' : 'opacity-0'} transition-opacity duration-1000 delay-1000`}>
          <div className="flex flex-wrap justify-center gap-3">
            {heroData.techStack.map((tech, index) => (
              <span 
                key={tech}
                className="px-3 py-1 bg-gray-100 dark:bg-gray-800/80 rounded-full text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                style={{ 
                  animationDelay: `${index * 100 + 1000}ms`,
                  animation: hasAnimated ? 'fadeIn 0.5s ease forwards' : 'none'
                }}
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
      
      {/* Add the scroll down indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center animate-bounce">
        <span className="text-gray-500 dark:text-gray-400 text-sm mb-2">Scroll Down</span>
        <svg className="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
      
      {/* Replace the <style jsx> with a regular <style> */}
      <style>
        {`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        `}
      </style>
    </div>
  );
};

export default HomePage;